import React, {
  useState, useMemo, useEffect, useId,
} from 'react';
import { useFetch } from './api';

const TYPES = [
  {
    value: 'wms',
    label: 'WMS / 3PL',
    nameLabel: 'WMS / 3PL name',
    contactLabel: 'WMS contact email',
    flowNoun: 'your WMS',
  },
  {
    value: 'cart',
    label: 'Cart',
    nameLabel: 'Cart / platform name',
    contactLabel: 'Cart contact email',
    flowNoun: 'your cart',
  },
  {
    value: 'carrier',
    label: 'Carrier',
    nameLabel: 'Carrier name',
    contactLabel: 'Carrier contact email',
    flowNoun: 'your carrier',
  },
];

const NOT_SURE_VALUE = 'not_sure';

const LABEL_CLS = 'block text-xs font-medium text-surface-500 uppercase tracking-wider mb-1.5';
const HELPER_CLS = 'text-xs text-surface-400 mt-1';

const URL_SHAPE = /^(https?:\/\/)?\S+\.\S+$/i;

const ensureScheme = (url) => {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

function Field({
  id, label, required, children, helper,
}) {
  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label htmlFor={id} className={LABEL_CLS}>
        {label}
        {required && ' *'}
      </label>
      {children}
      {helper && <p className={HELPER_CLS}>{helper}</p>}
    </div>
  );
}

function TextInput({
  id, value, onChange, type = 'text', placeholder, required, pattern, title, disabled,
}) {
  return (
    <input
      id={id}
      type={type}
      className="input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      pattern={pattern}
      title={title}
      disabled={disabled}
    />
  );
}

function Checkbox({ checked, onChange, label }) {
  const id = useId();
  return (
    <div className="inline-flex items-center gap-2">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="cursor-pointer" />
      <label htmlFor={id} className="text-sm text-surface-700 cursor-pointer">{label}</label>
    </div>
  );
}

function Radio({
  name, value, checked, onChange, label,
}) {
  const id = useId();
  return (
    <div className="inline-flex items-center gap-2">
      <input id={id} type="radio" name={name} value={value} checked={checked} onChange={() => onChange(value)} className="cursor-pointer" />
      <label htmlFor={id} className="text-sm text-surface-700 cursor-pointer">{label}</label>
    </div>
  );
}

function FlowCheckboxGrid({ flows, selectedFlows, onToggle }) {
  return (
    <div className="grid grid-cols-2 gap-y-1.5 gap-x-4">
      {flows.map((flow) => (
        <Checkbox
          key={flow.value}
          checked={selectedFlows.includes(flow.value)}
          onChange={() => onToggle(flow.value)}
          label={flow.label}
        />
      ))}
    </div>
  );
}

function FlowsPicker({
  flowsLoading, flowNoun, flowOptions, selectedFlows, onToggle,
}) {
  if (flowsLoading) {
    return <p className="text-sm text-surface-400">Loading flows...</p>;
  }
  const notSure = flowOptions.find((f) => f.value === NOT_SURE_VALUE);
  const groups = [
    {
      heading: `Read flows — we pull data from ${flowNoun}`,
      flows: flowOptions.filter((f) => f.value !== NOT_SURE_VALUE && f.direction === 'read'),
    },
    {
      heading: `Write flows — we send data to ${flowNoun} (requires sandbox)`,
      flows: flowOptions.filter((f) => f.direction === 'write'),
    },
  ].filter((g) => g.flows.length > 0);

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.heading}>
          <div className="text-xs font-medium text-surface-600 mb-2">{group.heading}</div>
          <FlowCheckboxGrid
            flows={group.flows}
            selectedFlows={selectedFlows}
            onToggle={onToggle}
          />
        </div>
      ))}
      {notSure && (
        <div>
          <Checkbox
            checked={selectedFlows.includes(notSure.value)}
            onChange={() => onToggle(notSure.value)}
            label={notSure.label}
          />
        </div>
      )}
    </div>
  );
}

const initialForm = {
  integrationType: 'wms',
  integrationName: '',
  integrationContactEmail: '',
  brandName: '',
  brandContactEmail: '',
  hasInfoYet: 'yes',
  apiDocsMode: 'url',
  apiDocsUrl: '',
  flows: [],
  prodCredsUrl: '',
  sandboxCredsUrl: '',
  notes: '',
};

function buildRequestBody(form) {
  const hasInfo = form.hasInfoYet === 'yes';
  const body = {
    integration_type: form.integrationType,
    integration_name: form.integrationName.trim(),
    integration_contact_email: form.integrationContactEmail.trim() || null,
    brand_name: form.brandName.trim() || null,
    brand_contact_email: form.brandContactEmail.trim() || null,
    flows: form.flows,
    notes: form.notes.trim() || null,
  };
  if (hasInfo) {
    if (form.apiDocsMode === 'email') {
      body.api_docs_via_email = true;
    } else {
      body.api_docs_url = ensureScheme(form.apiDocsUrl);
    }
    body.prod_creds_url = ensureScheme(form.prodCredsUrl);
    if (form.sandboxCredsUrl.trim()) {
      body.sandbox_creds_url = ensureScheme(form.sandboxCredsUrl);
    }
  }
  return body;
}

function IntegrationRequestModal({ isOpen, onClose, onSuccess }) {
  const [form, setForm] = useState(initialForm);
  const [flowOptions, setFlowOptions] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const trackstar = useFetch();

  const typeConfig = useMemo(
    () => TYPES.find((t) => t.value === form.integrationType) ?? TYPES[0],
    [form.integrationType],
  );

  const sandboxRequired = useMemo(
    () => form.flows.includes(NOT_SURE_VALUE)
      || form.flows.some((f) => flowOptions.find((x) => x.value === f)?.direction === 'write'),
    [form.flows, flowOptions],
  );

  const isReady = form.hasInfoYet === 'yes';

  useEffect(() => {
    let cancelled = false;
    setFlowsLoading(true);
    trackstar(`internal/integration-requests/flows?integration_type=${form.integrationType}`)
      .then((data) => {
        if (cancelled) return;
        setFlowOptions(data?.flows ?? []);
      })
      .catch(() => {
        if (!cancelled) setFlowOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFlowsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.integrationType]);

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setIntegrationType = (value) => setForm((prev) => ({
    ...prev,
    integrationType: value,
    flows: [],
  }));

  const toggleFlow = (flow) => setForm((prev) => ({
    ...prev,
    flows: prev.flows.includes(flow)
      ? prev.flows.filter((f) => f !== flow)
      : [...prev.flows, flow],
  }));

  const handleClose = () => {
    setForm(initialForm);
    setError(null);
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.flows.length === 0) {
      setError('Select at least one flow.');
      return;
    }
    if (isReady) {
      const urlFields = [];
      if (form.apiDocsMode === 'url') {
        urlFields.push(['API documentation URL', form.apiDocsUrl, true]);
      }
      urlFields.push(['Production credentials URL', form.prodCredsUrl, true]);
      urlFields.push(['Sandbox credentials URL', form.sandboxCredsUrl, sandboxRequired]);
      const badUrl = urlFields.find(([, v, req]) => (req || v.trim()) && !URL_SHAPE.test(v.trim()));
      if (badUrl) {
        setError(`${badUrl[0]} doesn't look valid — include a domain like "docs.example.com".`);
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const rsp = await trackstar('internal/integration-requests', 'POST', buildRequestBody(form));
      // the fetch helper resolves on a 4xx too, so a rejected request lands here, not the catch
      if (!rsp?.success) {
        setError('Something went wrong. Please try again or email support@trackstarhq.com.');
        return;
      }
      setForm(initialForm);
      onSuccess();
      onClose();
    } catch (e) {
      setError('Something went wrong. Please try again or email support@trackstarhq.com.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="modal-backdrop"
        onClick={handleClose}
        onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
        tabIndex={-1}
        aria-hidden="true"
      />
      <div className="modal !max-w-2xl">
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-surface-900">Request new integration</h2>
          <p className="text-xs text-surface-500 mt-1">
            Tell us what you need. We&rsquo;ll open a ticket and follow up.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="modal-body space-y-6">

            <section>
              <div className={LABEL_CLS}>Do you have docs and creds yet? *</div>
              <div className="flex gap-6">
                <Radio name="hasInfoYet" value="yes" checked={form.hasInfoYet === 'yes'} onChange={setField('hasInfoYet')} label="Yes, I have docs and creds" />
                <Radio name="hasInfoYet" value="no" checked={form.hasInfoYet === 'no'} onChange={setField('hasInfoYet')} label="Not yet" />
              </div>
              {!isReady && (
                <div
                  className={
                    'mt-3 text-sm text-surface-700 bg-amber-50 border '
                    + 'border-amber-200 rounded-md px-3 py-2'
                  }
                >
                  We can&rsquo;t start the build without API docs, valid prod credentials, and a
                  contact at the 3PL. Submit what you have and we&rsquo;ll follow up on Slack
                  to collect the rest.
                </div>
              )}
            </section>

            <div className="border-t border-surface-100" />

            <section aria-labelledby="about-heading">
              <h3 id="about-heading" className="text-sm font-semibold text-surface-800 mb-3">
                About the integration
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Field id="integrationType" label="Integration type" required>
                  <select
                    id="integrationType"
                    className="input cursor-pointer"
                    value={form.integrationType}
                    onChange={(e) => setIntegrationType(e.target.value)}
                  >
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </Field>
                <div />
                <Field id="integrationName" label={typeConfig.nameLabel} required>
                  <TextInput id="integrationName" value={form.integrationName} onChange={setField('integrationName')} required />
                </Field>
                <Field
                  id="integrationContactEmail"
                  label={typeConfig.contactLabel}
                  helper="We'll need a contact to close out the integration. If you don't have it yet, we'll follow up."
                >
                  <TextInput id="integrationContactEmail" type="email" value={form.integrationContactEmail} onChange={setField('integrationContactEmail')} placeholder="api@example.com" />
                </Field>
                <Field id="brandName" label="Brand name">
                  <TextInput id="brandName" value={form.brandName} onChange={setField('brandName')} />
                </Field>
                <Field id="brandContactEmail" label="Brand contact email">
                  <TextInput id="brandContactEmail" type="email" value={form.brandContactEmail} onChange={setField('brandContactEmail')} />
                </Field>
              </div>
            </section>

            <div className="border-t border-surface-100" />

            <section aria-labelledby="details-heading">
              <h3 id="details-heading" className="text-sm font-semibold text-surface-800 mb-3">
                Build details
              </h3>

              <div>
                <div className={LABEL_CLS}>Flows needed *</div>
                <FlowsPicker
                  flowsLoading={flowsLoading}
                  flowNoun={typeConfig.flowNoun}
                  flowOptions={flowOptions}
                  selectedFlows={form.flows}
                  onToggle={toggleFlow}
                />
              </div>

              {isReady && (
                <>
                  <div className="mt-4">
                    <div className={LABEL_CLS}>API documentation *</div>
                    <div className="flex gap-6 mb-2">
                      <Radio name="apiDocsMode" value="url" checked={form.apiDocsMode === 'url'} onChange={setField('apiDocsMode')} label="Paste URL" />
                      <Radio name="apiDocsMode" value="email" checked={form.apiDocsMode === 'email'} onChange={setField('apiDocsMode')} label="I'll email it as an attachment" />
                    </div>
                    {form.apiDocsMode === 'url' ? (
                      <TextInput
                        id="apiDocsUrl"
                        value={form.apiDocsUrl}
                        onChange={setField('apiDocsUrl')}
                        placeholder="docs.examplewms.com"
                        pattern={URL_SHAPE.source}
                        title="Include a domain like docs.example.com"
                        required
                      />
                    ) : (
                      <p className="text-sm text-surface-600 bg-surface-50 border border-surface-200 rounded-md px-3 py-2">
                        Send the docs (PDF, etc.) as an attachment to{' '}
                        <span className="font-medium">integrations@trackstarhq.com</span>{' '}
                        and reference this integration name.
                      </p>
                    )}
                  </div>

                  <div className="mt-4">
                    <Field
                      id="prodCredsUrl"
                      label="Production credentials (one-time secret link)"
                      required
                      helper={(
                        <>
                          Share securely via
                          {' '}
                          <a href="https://onetimesecret.com" target="_blank" rel="noopener noreferrer" className="underline">
                            onetimesecret.com
                          </a>
                          .
                        </>
                      )}
                    >
                      <p className="text-xs text-surface-500 mb-2">
                        Production credentials allow us to validate data shapes in production,
                        which can differ from sandbox testing. Without these, we may need to fix
                        additional issues that can arise after your customer initially connects.
                      </p>
                      <TextInput
                        id="prodCredsUrl"
                        value={form.prodCredsUrl}
                        onChange={setField('prodCredsUrl')}
                        placeholder="onetimesecret.com/..."
                        pattern={URL_SHAPE.source}
                        title="Include a domain like onetimesecret.com/abc123"
                        required
                      />
                    </Field>
                  </div>

                  <div className="mt-4">
                    <Field
                      id="sandboxCredsUrl"
                      label="Sandbox credentials (one-time secret link)"
                      required={sandboxRequired}
                      helper={sandboxRequired
                        ? 'Required because you selected a write flow — we need a sandbox to test against before writing to production.'
                        : 'Provide if you might add write flows later — we need a sandbox to test against before writing to production.'}
                    >
                      <TextInput
                        id="sandboxCredsUrl"
                        value={form.sandboxCredsUrl}
                        onChange={setField('sandboxCredsUrl')}
                        placeholder="onetimesecret.com/..."
                        pattern={form.sandboxCredsUrl ? URL_SHAPE.source : undefined}
                        title="Include a domain like onetimesecret.com/abc123"
                        required={sandboxRequired}
                      />
                    </Field>
                  </div>
                </>
              )}

              <div className="mt-4">
                <Field id="notes" label="Anything else we should know?">
                  <textarea
                    id="notes"
                    className="input"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField('notes')(e.target.value)}
                  />
                </Field>
              </div>
            </section>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" onClick={handleClose} className="btn btn-secondary" disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-accent" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default IntegrationRequestModal;
